import { setProduct } from "./productAction";

// Catalogue global des produits canoniques, et adoption d'un de ces produits
// par le compte connecté.
//
// Les deux appels passent par les relais Express /stocks/catalogue et
// /stocks/catalogue/adopt : le navigateur ne parle jamais au storehouse
// directement, et c'est le relais qui transmet l'identité de l'utilisateur.

// Messages de repli, employés quand le serveur ne fournit pas le sien.
function errorMessage(status, body, defaut) {
    if (body && body.error && body.error.message) {
        return body.error.message
    }
    if (status === 401) {
        return "Session expirée ou invalide : merci de vous reconnecter."
    }
    if (status === 403) {
        return "Accès refusé."
    }
    if (status === 404) {
        return "Ce produit n'existe plus dans le catalogue."
    }
    return defaut + " (erreur " + status + ")."
}

// Le 403 sans corps (utilisateur non authentifié) n'est pas du JSON.
async function readBody(response) {
    try {
        return await response.json()
    } catch (parseError) {
        return {}
    }
}

/**
 * Charge le catalogue des produits canoniques adoptables.
 *
 * La réponse ne porte que l'identité des produits : ni prix, ni marge, ni
 * remise. Un compte qui parcourt le catalogue ne doit pas y lire la politique
 * tarifaire d'un autre.
 */
export function fetchCatalogue() {
    return async function (dispatch) {
        dispatch({
            type: "FETCH_CATALOGUE_START",
            payload: ""
        })

        try {
            const response = await fetch('/stocks/catalogue', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'same-origin',
            })

            const body = await readBody(response)

            if (!response.ok || (body && body.error)) {
                dispatch({
                    type: "FETCH_CATALOGUE_ERROR",
                    payload: errorMessage(response.status, body, "Impossible de charger le catalogue"),
                })
                return
            }

            dispatch({
                type: "FETCH_CATALOGUE_FULFILLED",
                payload: Array.isArray(body) ? body : [],
            })

        } catch (err) {
            console.log("[ERROR] fetchCatalogue ", err)
            dispatch({
                type: "FETCH_CATALOGUE_ERROR",
                payload: "Impossible de joindre le serveur.",
            })
        }
    }
}

// Relit la seule fiche adoptée et la pose dans le store.
//
// /stocks/single sert déjà les produits à l'unité et applique les réglages du
// compte au produit rendu : on obtient donc la fiche telle que le serveur la
// sert, valeurs commerciales comprises, et non un objet reconstitué ici.
//
// Rien de ce qui se passe ici ne doit toucher inventory.state.error. Cet état
// est lu par StockDashboard comme une panne globale : il remplace tout le
// module par « Magasin Indisponible », et seul un chargement réussi l'efface,
// or plus aucun écran n'est monté pour en déclencher un. Une relecture
// manquée ne coûte qu'un rafraîchissement de page ; l'adoption, elle, a
// réussi, et la présenter comme une panne serait à la fois faux et coûteux.
//
// @returns {boolean} vrai si le store porte désormais la fiche.
async function relireProduitAdopte(dispatch, productID) {
    try {
        const response = await fetch('/stocks/single', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                page: "products",
                id: productID,
            }),
            credentials: 'same-origin',
        })

        const produit = await readBody(response)

        if (!response.ok || !produit || produit.error || !produit.id) {
            console.log("[ERROR] relecture du produit adopté ", productID, produit)
            return false
        }

        // src est le cache local des photos, que formatDataProds pose sur
        // chaque fiche venant du serveur.
        produit.src = []

        dispatch(setProduct(produit))
        return true

    } catch (err) {
        console.log("[ERROR] relecture du produit adopté ", err)
        return false
    }
}

/**
 * Ajoute un produit du catalogue au compte connecté, en lui donnant un prix.
 *
 * @param {string} productID identifiant du produit canonique.
 * @param {object} prix prix_venteHT, prix_venteTTC, tva_rate, reduc_max.
 * @returns {object} product_id, l'identifiant réellement adopté tel que le
 *          serveur le renvoie — une fiche absorbée est redirigée vers sa
 *          canonique plutôt que refusée, il peut donc différer de celui
 *          envoyé — et rafraichi, faux quand la fiche n'a pas pu être relue et
 *          que l'écran devra le dire à l'utilisateur.
 *
 * Échoue en levant une erreur, comme deleteProduct : l'appel vient d'un geste
 * explicite de l'utilisateur, qui attend un message immédiat et non un état
 * d'erreur silencieux dans le store.
 */
export function adoptCatalogueProduct(productID, prix) {
    return async function (dispatch) {
        dispatch({
            type: "ADOPT_CATALOGUE_START",
            payload: productID
        })

        let body = {}
        let response = null

        try {
            response = await fetch('/stocks/catalogue/adopt', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_id: productID,
                    prix_venteHT: prix.prix_venteHT,
                    prix_venteTTC: prix.prix_venteTTC,
                    tva_rate: prix.tva_rate,
                    reduc_max: prix.reduc_max,
                }),
                credentials: 'same-origin',
            })

            body = await readBody(response)

        } catch (err) {
            console.log("[ERROR] adoptCatalogueProduct ", err)
            dispatch({
                type: "ADOPT_CATALOGUE_ERROR",
                payload: "Impossible de joindre le serveur.",
            })
            throw new Error("Impossible de joindre le serveur.")
        }

        if (!response.ok || (body && body.error)) {
            const message = errorMessage(response.status, body, "L'ajout du produit a échoué")
            dispatch({
                type: "ADOPT_CATALOGUE_ERROR",
                payload: message,
            })
            throw new Error(message)
        }

        const adopte = body.product_id || productID

        dispatch({
            type: "ADOPT_CATALOGUE_FULFILLED",
            payload: adopte,
        })

        // La liste des produits du compte est alimentée une seule fois, au
        // montage de l'écran Stock : sans cette relecture, le produit adopté
        // n'existerait dans le store qu'au prochain rechargement de la page.
        // On ne relit que lui, et jamais tout l'inventaire : adopter dix
        // produits d'affilée est l'usage visé, pas dix rechargements complets.
        const rafraichi = await relireProduitAdopte(dispatch, adopte)

        return { product_id: adopte, rafraichi }
    }
}
